import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Select, MenuItem, FormControl, InputLabel,
  Button, Chip, List, ListItem, ListItemText, ListItemSecondaryAction,
  IconButton, Divider, CircularProgress, Grid,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import client from '../api/client';
import type { NodeResponse, UserResponse, AssignmentResponse, ApiResponse } from '../api/types';

export function AssignmentEditor() {
  const [nodes, setNodes] = useState<NodeResponse[]>([]);
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [assignments, setAssignments] = useState<AssignmentResponse[]>([]);
  const [selectedNode, setSelectedNode] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    const [nodesRes, usersRes, assignmentsRes] = await Promise.all([
      client.get('/nodes') as Promise<ApiResponse<NodeResponse[]>>,
      client.get('/users') as Promise<ApiResponse<UserResponse[]>>,
      client.get('/assignments') as Promise<ApiResponse<AssignmentResponse[]>>,
    ]);
    setNodes(nodesRes.data || []);
    setUsers(usersRes.data || []);
    setAssignments(assignmentsRes.data || []);
    setLoading(false);
  };

  const handleAddAssignment = async () => {
    if (!selectedNode || !selectedUser) return;
    await client.post('/assignments', {
      assignments: [{ nodeId: selectedNode, userId: selectedUser }],
    });
    setSelectedUser('');
    loadData();
  };

  const handleDeleteAssignment = async (id: string) => {
    await client.delete(`/assignments/${id}`);
    loadData();
  };

  const getNodeName = (nodeId: string) => nodes.find(n => n.id === nodeId)?.name || nodeId;
  const getUserName = (userId: string) => users.find(u => u.id === userId)?.displayName || userId;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={5}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>添加分配</Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>选择节点</InputLabel>
            <Select
              value={selectedNode}
              label="选择节点"
              onChange={e => setSelectedNode(e.target.value)}
            >
              {nodes.filter(n => n.isActive).map(node => (
                <MenuItem key={node.id} value={node.id}>
                  {node.name} ({node.protocol.toUpperCase()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>选择用户</InputLabel>
            <Select
              value={selectedUser}
              label="选择用户"
              onChange={e => setSelectedUser(e.target.value)}
            >
              {users.filter(u => u.isActive).map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.displayName} ({user.synoUsername})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddAssignment}
            disabled={!selectedNode || !selectedUser}
            fullWidth
          >
            分配节点
          </Button>
        </Paper>
      </Grid>

      <Grid item xs={12} md={7}>
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            当前分配列表 ({assignments.length})
          </Typography>
          {assignments.length === 0 ? (
            <Typography color="text.secondary">暂未分配任何节点</Typography>
          ) : (
            <List>
              {assignments.map((a, i) => (
                <Box key={a.id}>
                  {i > 0 && <Divider />}
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip label={getNodeName(a.nodeId)} size="small" color="primary" />
                          <Typography variant="body2" color="text.secondary">→</Typography>
                          <Chip label={a.userName || a.departmentName || '—'} size="small" />
                        </Box>
                      }
                      secondary={`分配时间: ${new Date(a.assignedAt).toLocaleString('zh-CN')}`}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        color="error"
                        onClick={() => handleDeleteAssignment(a.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Box>
              ))}
            </List>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}
